import { LoadingOverlay, Skeleton, Stack } from '@mantine/core';
import { t } from 'i18next';
import { Fragment, useMemo, useRef } from 'react';
import type {
  AllocationOfCostsCategory,
  AllocationOfCostsChartResponseByCategory,
} from '../../../../api/financials-client/financials-client.type';
import { formatFloraAllocationOfCostsData } from '../../../../components/allocation-of-costs/allocation-of-costs.helpers';
import { ContentPlaceholder } from '../../../../components/content-placeholder/content-placeholder';
import { exportChart } from '../../../../helpers/image-downloader/image-downloader';
import { useFinancialsStore } from '../../../../store/financials-store/financials-store';
import { setAllocationOfCostsChartType } from '../../../../store/financials-store/financials-store.actions';
import { ChartType } from '../../../../store/financials-store/financials-store.type';
import { secondaryBase } from '../../../../styles/design-tokens';
import { FloraButton } from '../../../flora/flora-button';
import { ChartDisplayTypeButtonSet } from '../shared/chart-display-type-button-set';
import { Chart as BarChart } from './bar-chart';
import { Chart as LineChart } from './line-chart';

interface ChartProps {
  data?: AllocationOfCostsChartResponseByCategory;
  isPending: boolean;
}

function Chart({ data, isPending }: ChartProps) {
  const selectedChartType = useFinancialsStore((state) => state.filters.allocationOfCosts.selectedChartType);
  const selectedCategory = useFinancialsStore((state) => state.filters.allocationOfCosts.selectedMeasure);
  const chartRef = useRef<HTMLDivElement>(null!);

  const chart = useMemo(() => {
    if (selectedChartType === ChartType.Line) {
      return <LineChart data={data} chartRef={chartRef} />;
    }
    if (selectedChartType === ChartType.Bar) {
      return <BarChart data={data} chartRef={chartRef} />;
    }
    if (isPending) {
      return <Skeleton />;
    }
    return null;
  }, [data, selectedChartType, isPending]);

  return (
    <Stack h="100%" style={{ overflow: 'hidden', position: 'relative' }}>
      <LoadingOverlay visible={isPending} loaderProps={{ color: secondaryBase }} />
      {!isPending && Object.keys(data || {}).length === 0 ? (
        <ContentPlaceholder icon="event_busy" text={t('common.labels.no_filtered_data')} />
      ) : (
        <Fragment>
          <ChartDisplayTypeButtonSet
            onSetLineChart={() => setAllocationOfCostsChartType(ChartType.Line)}
            onSetBarChart={() => setAllocationOfCostsChartType(ChartType.Bar)}
            selectedChartType={selectedChartType}
            showSelectViewBy
            onExportChart={() => exportChart(chartRef.current, 'allocation-of-costs-chart.png')}
            floraButton={
              <FloraButton
                externalData={[formatFloraAllocationOfCostsData(data, selectedCategory as AllocationOfCostsCategory)]}
                disabled={!data}
              />
            }
          />
          {chart}
        </Fragment>
      )}
    </Stack>
  );
}

export { Chart };
